import React, { useState } from 'react';
import { pick } from 'lodash/fp';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import Select from 'components/shared/select';
import TagImage from 'components/TagImage';
import { Button } from 'components/ui';

/* eslint-disable jsx-a11y/no-autofocus */

const TAG_LIBRARY_VISIBILITY_OPTIONS = [
  'excluded_from_library',
  'included_in_library',
  'featured_in_library',
];

const EditTag = ({
  tag,
  hasLearningLibrary,
  allowImageLibrary,
  canManageContentLibrary,
}) => {
  const defaultValues = {
    name: '',
    description: '',
    library_visibility: 'excluded_from_library',
  };

  const [tagData, setTagData] = useState(
    tag ? pick(Object.keys(defaultValues), tag) : defaultValues,
  );
  const [loading, setLoading] = useState(false);

  const isPristine = () => {
    const originalValues = tag || defaultValues;
    return Object.keys(defaultValues).every(
      key => originalValues[key] === tagData[key],
    );
  };

  const updateField = (fieldName, value) => {
    setTagData({ ...tagData, [fieldName]: value });
  };

  const updateTag = () => {
    setLoading(true);

    request
      .patchJSON(routes.TAGS_UPDATE(tag.id), tagData)
      .then(() => {
        setLoading(false);
        window.location = routes.TAGS;
      })
      .catch(() => {
        // Errors are flash notices via the server, so reload to show
        window.location.reload();
      });
  };

  const createTag = () => {
    setLoading(true);

    request
      .postJSON(routes.TAGS, tagData)
      .then(() => {
        setLoading(false);
        window.location = routes.TAGS;
      })
      .catch(() => {
        // Errors are flash notices via the server, so reload to show
        window.location.reload();
      });
  };

  return (
    <div className="panel row" data-back-title="" data-back-level="1">
      <header className="panel-header-underlined">
        <h1 className="panel-title">{tag ? t('tags.edit') : t('tags.new')}</h1>
      </header>

      <div className="DataTable TagsTable">
        <div className="DataTable-thead">
          <div className="DataTable-th DataTable-name">
            {t('tags.table.name')}
          </div>

          {hasLearningLibrary && (
            <div className="DataTable-th">{t('learn.tag_title')}</div>
          )}

          {tag && (
            <div className="DataTable-th">{t('tags.table.cover_image')}</div>
          )}

          <div className="DataTable-th DataTable-description">
            {t('tags.table.description')}
          </div>

          <div className="DataTable-th DataTable-actions" />
        </div>

        <form className="DataTable-tr">
          <div className="DataTable-td DataTable-name controls">
            <label className="sr-only" htmlFor="tag_name">
              {t('tags.table.name')}
            </label>
            <input
              data-required="true"
              autoFocus="autofocus"
              type="text"
              name="tag[name]"
              id="tag_name"
              onChange={event => updateField('name', event.target.value)}
              value={tagData.name}
            />
          </div>
          {hasLearningLibrary && (
            <div className="DataTable-td">
              <Select
                name="tag[library_visibility]"
                id="tag_library_visibility"
                value={tagData.library_visibility}
                onChange={event =>
                  updateField('library_visibility', event.target.value)
                }
              >
                {TAG_LIBRARY_VISIBILITY_OPTIONS.map(key => (
                  <option key={key} value={key}>
                    {t(`tags.learn.${key}`)}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {tag && (
            <div className="DataTable-td">
              <TagImage
                content_type="Tag"
                image_type="cover"
                tag={tag}
                uploadTooltip={t('taggings.upload_tooltip')}
                allowImageLibrary={allowImageLibrary}
                meta={{
                  permissions: {
                    can_manage_content_library: canManageContentLibrary,
                  },
                }}
              />
            </div>
          )}
          <div className="DataTable-td DataTable-description controls">
            <textarea
              data-behavior="autosize"
              name="tag[description]"
              id="tag_description"
              onChange={event => updateField('description', event.target.value)}
              value={tagData.description}
            />
          </div>
          <div className="DataTable-td DataTable-actions">
            <Button
              name="commit"
              type="button"
              className="btn btn-primary legacyButton"
              onClick={tag ? updateTag : createTag}
              customColor
              smaller
              loading={loading}
              disabled={
                tag ? isPristine() : tagData.name.trim() === defaultValues.name
              }
            >
              {tag ? t('tags.update_tag') : t('tags.create_tag')}
            </Button>
            <Button smaller className="btn legacyButton" url="/tags">
              {t('tags.cancel')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default props => <EditTag {...props} />;
